import type { CaseManifestV1 } from "@cold-case/shared/case-manifest";
import Phaser from "phaser";
import {
  isGuestDisplayName,
  worldLabelForPlayer,
} from "@cold-case/shared/player-display-name";
import type { PlayerDisplayMember } from "@cold-case/shared/player-display-name";

import { npcDisplayName } from "../lib/manifest-display.js";
import { UNDISCOVERED_POI_LABEL } from "./case-files-map.js";
import { UI_FONT } from "./ui-theme.js";

export { isGuestDisplayName, worldLabelForPlayer };
export type SessionPlayerLabel = PlayerDisplayMember;

/** Gap between sprite top and label (foot-anchored sprites). */
export const WORLD_NAMEPLATE_GAP_PX = 12;

export const LOCAL_PLAYER_NAMEPLATE_COLOR = "#eab308";
export const TEAMMATE_NAMEPLATE_COLOR = "#c084fc";
export const NPC_NAMEPLATE_COLOR = "#f87171";

const LOCAL_NAMEPLATE_FONT_PX = 15;
const REMOTE_NAMEPLATE_FONT_PX = 13;

export function nameplateLocalYAboveSprite(
  sprite: Phaser.GameObjects.Sprite | Phaser.GameObjects.Image
): number {
  return (
    -sprite.displayHeight * sprite.originY - WORLD_NAMEPLATE_GAP_PX
  );
}

type NameplateStyle = {
  color: string;
  stroke: string;
  fontPx: number;
};

function nameplateStyle(localViewer: boolean): NameplateStyle {
  if (localViewer) {
    return {
      color: LOCAL_PLAYER_NAMEPLATE_COLOR,
      stroke: "#422006",
      fontPx: LOCAL_NAMEPLATE_FONT_PX,
    };
  }
  return {
    color: TEAMMATE_NAMEPLATE_COLOR,
    stroke: "#1e1033",
    fontPx: REMOTE_NAMEPLATE_FONT_PX,
  };
}

export function createWorldNameplateText(
  scene: Phaser.Scene,
  label: string,
  options: { localViewer?: boolean; npc?: boolean }
): Phaser.GameObjects.Text {
  const style = options.npc
    ? { color: NPC_NAMEPLATE_COLOR, stroke: "#1f0a0a", fontPx: REMOTE_NAMEPLATE_FONT_PX }
    : nameplateStyle(options.localViewer === true);
  const text = scene.add
    .text(0, 0, label, {
      fontSize: `${style.fontPx}px`,
      color: style.color,
      fontFamily: UI_FONT,
      stroke: style.stroke,
      strokeThickness: 1,
    })
    .setOrigin(0.5, 1);
  const withRes = text as Phaser.GameObjects.Text & {
    setResolution?: (value: number) => Phaser.GameObjects.Text;
  };
  withRes.setResolution?.(1);
  return text;
}

export function syncNameplateAboveSprite(
  nameplate: Phaser.GameObjects.Text,
  sprite: Phaser.GameObjects.Sprite | Phaser.GameObjects.Image
): void {
  nameplate.setY(Math.round(nameplateLocalYAboveSprite(sprite) + sprite.y));
}

/** Player-facing floating label — masked until case notes reveal the person. */
export function npcWorldNameplateLabel(
  manifest: CaseManifestV1,
  instanceId: string,
  journal: Record<string, unknown>
): string {
  const discovered = new Set((journal.discovered_npcs as string[]) ?? []);
  if (!discovered.has(instanceId)) return UNDISCOVERED_POI_LABEL;
  const n = manifest.npcs.find((x) => x.instance_id === instanceId);
  const raw = n?.name?.trim();
  if (raw) return raw;
  return npcDisplayName(manifest, instanceId);
}
